import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useStableValue, useStableValues } from '@opm/utils/stableKey';
import {
  getCommunityTimelineTop,
  ICommunityTimelineTopItem,
} from '@op/services/keyMetrics';
import { buildCommunityTimelineTopPayload } from '../constants/topPanel';

const EMPTY_LIST: ICommunityTimelineTopItem[] = [];

export function useCommunityTimelineTop({
  enabled,
  filter,
  tab,
  metric,
  clickParams,
}: {
  enabled: boolean;
  filter: Record<string, any>;
  tab: string;
  metric: string;
  clickParams: Record<string, any>;
}) {
  const [list, setList] = useState<ICommunityTimelineTopItem[]>(EMPTY_LIST);
  const clearList = useCallback(() => setList(EMPTY_LIST), []);
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: clearList,
  });

  const [stableFilter, stableClickParams] = useStableValues(
    filter,
    clickParams,
  );

  const params = useMemo(() => {
    if (!enabled || !stableFilter?.edition_unified_id) return null;
    if (!stableClickParams?.start_time && !stableClickParams?.end_time) {
      return null;
    }
    return buildCommunityTimelineTopPayload(stableFilter, {
      tab_type: tab,
      metric,
      clickParams: stableClickParams,
      limit: 3,
    });
  }, [enabled, stableFilter, tab, metric, stableClickParams]);

  function request(params: Record<string, any>) {
    wrapperCancelRequest(getCommunityTimelineTop, params)
      .then(data => {
        setList(data?.list || []);
      })
      .catch(() => {
        setList(EMPTY_LIST);
      });
  }

  useEffect(() => {
    if (!params) return;
    request(params);
  }, [params]);

  return { list, loading };
}
